import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Chapter } from "./Chapter";
import { Reveal } from "./Reveal";
import { instagram, socials, photos } from "../data/siteConfig";
import { socialPendingToast } from "../lib/notify";
import { LineCat } from "./CatCrest";
import { InstagramIcon } from "./SocialIcons";
import { X, Play } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Gallery = () => {
    const [selected, setSelected] = useState(null);
    const [ig, setIg] = useState(null); // null = loading, {connected, items}
    const insta = socials.find((s) => s.id === "instagram");
    const liveItems = ig?.connected && ig?.items?.length ? ig.items : [];
    const isLive = liveItems.length > 0;
    const hasPhotos = photos.gallery.length > 0;

    useEffect(() => {
        let cancelled = false;
        fetch(`${API}/instagram/feed`)
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((data) => !cancelled && setIg(data))
            .catch(() => !cancelled && setIg({ connected: false, items: [] }));
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <section
            className="bg-coal py-24 text-cream sm:py-32"
            data-testid="gallery-section"
        >
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Chapter
                    no="05"
                    id="gallery"
                    dark
                    eyebrow="Gallery"
                    title={
                        <>
                            Life at <em>Mākara.</em>
                        </>
                    }
                    intro={
                        isLive
                            ? `Fresh from ${instagram.accountName} on Instagram — this gallery updates automatically.`
                            : hasPhotos
                              ? "Real cats, real Mākara — photographs taken right here at the cattery."
                              : "We're gathering authentic photos of the cattery, our guests and the Mākara surroundings. When our Instagram is connected, recent photos and videos will appear here automatically."
                    }
                />

                {isLive ? (
                    <div
                        className="mt-14 columns-2 gap-4 md:columns-3"
                        data-testid="gallery-grid"
                    >
                        {liveItems.slice(0, 9).map((item, i) => (
                            <Reveal
                                key={item.id}
                                delay={0.04 * (i % 3)}
                                className="mb-4 break-inside-avoid"
                            >
                                <a
                                    href={item.permalink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="spotlight-frame group relative block w-full overflow-hidden rounded-2xl border border-[#7C8966]/25"
                                    data-testid={`ig-item-${i}`}
                                    aria-label={`View on Instagram: ${(item.caption || "Instagram post").slice(0, 80)}`}
                                >
                                    <img
                                        src={
                                            item.media_type === "VIDEO"
                                                ? item.thumbnail_url || item.media_url
                                                : item.media_url
                                        }
                                        alt={(item.caption || "Mākara Cattery on Instagram").slice(0, 140)}
                                        loading="lazy"
                                        className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                                    />
                                    {item.media_type === "VIDEO" && (
                                        <span className="absolute right-3 top-3 rounded-full bg-black/60 p-2 text-cream backdrop-blur-sm">
                                            <Play className="h-3.5 w-3.5 fill-current" />
                                        </span>
                                    )}
                                </a>
                            </Reveal>
                        ))}
                    </div>
                ) : hasPhotos ? (
                    <div
                        className="mt-14 columns-2 gap-4 md:columns-3"
                        data-testid="gallery-grid"
                    >
                        {photos.gallery.map((p, i) => (
                            <Reveal key={p.src} delay={0.04 * (i % 3)} className="mb-4 break-inside-avoid">
                                <button
                                    className="spotlight-frame group block w-full overflow-hidden rounded-2xl border border-[#7C8966]/25"
                                    onClick={() => setSelected(p)}
                                    data-testid={`gallery-item-${i}`}
                                    aria-label={`View photo: ${p.alt}`}
                                >
                                    <img
                                        src={p.src}
                                        alt={p.alt}
                                        loading="lazy"
                                        className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                                    />
                                </button>
                            </Reveal>
                        ))}
                    </div>
                ) : (
                    <div className="mt-14 grid gap-5 sm:grid-cols-3" data-testid="gallery-grid">
                        {[0, 1, 2].map((i) => (
                            <Reveal key={i} delay={0.07 * i}>
                                <div
                                    className="flex aspect-[4/3] flex-col items-center justify-center gap-4 rounded-3xl border border-dashed border-[#7C8966]/30 bg-[#2A2D24] p-8 text-center"
                                    data-testid={`gallery-placeholder-${i}`}
                                >
                                    <LineCat className="h-10 w-10 text-cream/20" />
                                    <p className="font-serif text-xl italic text-cream/50">
                                        More photos coming soon
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                )}

                {!isLive && (
                    <Reveal delay={0.1}>
                        <div
                            className="mt-10 flex flex-col items-start justify-between gap-6 rounded-3xl border border-[#7C8966]/25 bg-[#2A2D24] p-8 text-cream sm:flex-row sm:items-center sm:p-10"
                            data-testid="instagram-connect-note"
                        >
                            <div className="flex items-start gap-5">
                                <InstagramIcon className="h-8 w-8 shrink-0 text-cream" />
                                <div>
                                    <h3 className="font-serif text-2xl font-semibold">
                                        Photos & videos from Instagram, soon
                                    </h3>
                                    <p className="mt-2 max-w-lg text-sm leading-relaxed text-stone-300">
                                        Follow along at {instagram.accountName}{" "}
                                        for daily photos and videos of our
                                        guests — and when our Instagram is
                                        connected, this gallery will update
                                        itself automatically.
                                    </p>
                                </div>
                            </div>
                            {insta?.url ? (
                                <a
                                    href={insta.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="shrink-0 rounded-full bg-cream px-6 py-3 text-sm font-semibold text-[#1c1917] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-white"
                                    data-testid="instagram-follow-button"
                                >
                                    Follow {instagram.accountName}
                                </a>
                            ) : (
                                <button
                                    onClick={() => socialPendingToast("Instagram", instagram.accountName)}
                                    className="shrink-0 rounded-full bg-cream px-6 py-3 text-sm font-semibold text-[#1c1917] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-white"
                                    data-testid="instagram-follow-button"
                                >
                                    Follow {instagram.accountName}
                                </button>
                            )}
                        </div>
                    </Reveal>
                )}

                {isLive && (
                    <Reveal delay={0.1}>
                        <div className="mt-10 flex justify-center">
                            <a
                                href={insta?.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2.5 rounded-full bg-cream px-6 py-3 text-sm font-semibold text-[#1c1917] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-white"
                                data-testid="instagram-follow-button"
                            >
                                <InstagramIcon className="h-4 w-4" />
                                Follow {instagram.accountName}
                            </a>
                        </div>
                    </Reveal>
                )}
            </div>

            {/* Lightbox (local authentic photos) */}
            <AnimatePresence>
                {selected && (
                    <motion.div
                        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-5 backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setSelected(null)}
                        role="dialog"
                        aria-modal="true"
                        aria-label={selected.alt}
                        data-testid="gallery-lightbox"
                    >
                        <motion.figure
                            initial={{ scale: 0.92, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            className="max-h-[85vh] max-w-4xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={selected.src}
                                alt={selected.alt}
                                className="max-h-[75vh] w-full rounded-2xl object-contain"
                            />
                            <figcaption className="mt-4 text-center text-sm text-stone-300">
                                {selected.alt}
                            </figcaption>
                        </motion.figure>
                        <button
                            className="absolute right-5 top-5 rounded-full border border-white/20 p-3 text-cream transition-colors duration-200 hover:border-stone-400 hover:text-stone-300"
                            onClick={() => setSelected(null)}
                            aria-label="Close photo"
                            data-testid="gallery-lightbox-close"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};
